import React, { useState, useEffect, useRef, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Download,
  FileText,
  FileSpreadsheet,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Search,
  Upload,
  X,
  ChevronDown,
  ChevronRight,
  Filter,
  SortAsc,
  SortDesc,
  Users,
  Bus,
  User,
  MapPin,
  Clock,
  FileCheck,
  AlertCircle,
  CheckCircle2,
  Loader2
} from "lucide-react";
import vehicleService from "@/services/vehicleService";

const StudentVehicleMapping = () => {
  const [vehicleRoutes, setVehicleRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRoute, setExpandedRoute] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const fileInputRef = useRef(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [busFilter, setBusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  const fetchVehicleRoutes = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await vehicleService.getStudentMappingsByRoute();
      setVehicleRoutes(data.routeMappings || []);

      try {
        const stats = await vehicleService.getStudentMappingStatistics();
        setStatistics(stats);
      } catch (statsError) {
        console.warn("Failed to fetch statistics:", statsError);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch vehicle routes");
      console.error("Error fetching vehicle routes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicleRoutes();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, assignmentFilter, busFilter, sortBy, sortDirection]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    setError("");
    setImportResult(null);

    try {
      const result = await vehicleService.importStudentBusRouteMappings(file);
      
      setImportResult(result.data);
      setShowImportDialog(true);
      
      await fetchVehicleRoutes();
      
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to import student mappings";
      setError(errorMessage);
      console.error("Import error:", err);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const downloadTemplate = () => {
    const template = `Admission Number,Bus Route Name,Shift Name,Stop Name
INSTRUCTIONS: Fill in the following columns with your data. Delete this row and the next row before uploading.
REQUIRED: Student admission number,REQUIRED: Vehicle number (e.g. BUS-001),REQUIRED: Shift name (MUST match exactly),OPTIONAL: Stop name (leave empty to use first stop)
12345,BUS-001,Morning Shift,Stop 1
12346,BUS-001,Evening Shift,Stop 2
12347,BUS-002,Morning Shift,Main Gate
12348,BUS-002,Evening Shift,`;
    
    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "student_bus_route_mapping_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const students = filteredStudents;
    if (students.length === 0) return;

    const headers = [
      "Student Name",
      "Admission Number",
      "Email",
      "Phone",
      "Pickup Vehicle",
      "Pickup Vehicle Type",
      "Pickup Shift",
      "Pickup Stop",
      "Pickup Driver",
      "Drop Vehicle",
      "Drop Vehicle Type",
      "Drop Shift",
      "Drop Stop",
      "Drop Driver"
    ];

    let csvContent = headers.join(",") + "\n";

    students.forEach((student) => {
      const row = [
        `"${(student.studentName || "").replace(/"/g, '""')}"`,
        `"${(student.admissionNumber || "N/A").replace(/"/g, '""')}"`,
        `"${(student.studentEmail || "").replace(/"/g, '""')}"`,
        `"${(student.studentPhone || "").replace(/"/g, '""')}"`,
        `"${(student.pickup?.vehicleNumber || "N/A").replace(/"/g, '""')}"`,
        `"${(student.pickup?.vehicleType || "N/A").replace(/"/g, '""')}"`,
        `"${(student.pickup?.shiftName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.pickup?.stopName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.pickup?.driverName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.vehicleNumber || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.vehicleType || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.shiftName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.stopName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.driverName || "N/A").replace(/"/g, '""')}"`
      ];

      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `bus_data_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = (format = "xlsx") => {
    const students = filteredStudents;
    if (students.length === 0) return;

    const headers = [
      "Student Name",
      "Admission Number",
      "Email",
      "Phone",
      "Pickup Vehicle",
      "Pickup Vehicle Type",
      "Pickup Shift",
      "Pickup Stop",
      "Pickup Driver",
      "Drop Vehicle",
      "Drop Vehicle Type",
      "Drop Shift",
      "Drop Stop",
      "Drop Driver"
    ];

    let csvContent = headers.join(",") + "\n";

    students.forEach((student) => {
      const row = [
        `"${(student.studentName || "").replace(/"/g, '""')}"`,
        `"${(student.admissionNumber || "N/A").replace(/"/g, '""')}"`,
        `"${(student.studentEmail || "").replace(/"/g, '""')}"`,
        `"${(student.studentPhone || "").replace(/"/g, '""')}"`,
        `"${(student.pickup?.vehicleNumber || "N/A").replace(/"/g, '""')}"`,
        `"${(student.pickup?.vehicleType || "N/A").replace(/"/g, '""')}"`,
        `"${(student.pickup?.shiftName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.pickup?.stopName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.pickup?.driverName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.vehicleNumber || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.vehicleType || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.shiftName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.stopName || "N/A").replace(/"/g, '""')}"`,
        `"${(student.drop?.driverName || "N/A").replace(/"/g, '""')}"`
      ];

      csvContent += row.join(",") + "\n";
    });

    const ext = format === "xls" ? "xls" : "xlsx";
    const blob = new Blob([csvContent], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `bus_data_${new Date().toISOString().split("T")[0]}.${ext}`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getGroupedStudentMappings = () => {
    const studentMap = new Map();
    let totalMappingsProcessed = 0;

    vehicleRoutes.forEach((route) => {
      route.mappings?.forEach((mapping) => {
        totalMappingsProcessed++;
        
        const studentKey = mapping.studentId || mapping.studentEmail || mapping.studentName;
        
        if (!studentMap.has(studentKey)) {
          studentMap.set(studentKey, {
            studentId: mapping.studentId,
            studentName: mapping.studentName,
            studentEmail: mapping.studentEmail,
            studentPhone: mapping.studentPhone,
            admissionNumber: mapping.admissionNumber || "N/A",
            pickup: null,
            drop: null
          });
        }

        const student = studentMap.get(studentKey);
        const assignmentInfo = {
          vehicleNumber: route.vehicleNumber,
          vehicleType: route.vehicleType,
          shiftName: mapping.shiftName,
          stopName: mapping.stopName,
          stopLatitude: mapping.stopLatitude,
          stopLongitude: mapping.stopLongitude,
          driverName: route.driverName,
        };

        if (mapping.pickupDrop === "Pickup") {
          student.pickup = assignmentInfo;
        } else if (mapping.pickupDrop === "Drop") {
          student.drop = assignmentInfo;
        }
      });
    });

    const grouped = Array.from(studentMap.values());
    console.log(`StudentVehicleMapping: Processed ${totalMappingsProcessed} total mappings, grouped into ${grouped.length} unique students`);
    
    return grouped;
  };

  const groupedStudents = getGroupedStudentMappings();

  const uniqueVehicleNumbers = useMemo(() => {
    const vehicles = new Set();
    vehicleRoutes.forEach(route => {
      if (route.vehicleNumber && route.vehicleNumber !== "N/A") {
        vehicles.add(route.vehicleNumber);
      }
    });
    return Array.from(vehicles).sort();
  }, [vehicleRoutes]);

  const filteredStudents = groupedStudents.filter((student) => {
    const hasPickup = !!student.pickup;
    const hasDrop = !!student.drop;
    const isAssigned = hasPickup || hasDrop;
    
    switch (assignmentFilter) {
      case "drop-only":
        if (!hasDrop || hasPickup) return false;
        break;
      case "pickup-only":
        if (!hasPickup || hasDrop) return false;
        break;
      case "assigned":
        if (!isAssigned) return false;
        break;
      case "unassigned":
        if (isAssigned) return false;
        break;
      case "all":
      default:
        break;
    }
    
    if (busFilter !== "all") {
      const matchesBus = 
        (student.pickup?.vehicleNumber === busFilter) ||
        (student.drop?.vehicleNumber === busFilter);
      if (!matchesBus) return false;
    }
    
    if (searchTerm && searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        (student.studentName || "").toLowerCase().includes(searchLower) ||
        (student.admissionNumber || "").toLowerCase().includes(searchLower) ||
        (student.studentEmail || "").toLowerCase().includes(searchLower) ||
        (student.pickup?.vehicleNumber || "").toLowerCase().includes(searchLower) ||
        (student.drop?.vehicleNumber || "").toLowerCase().includes(searchLower) ||
        (student.pickup?.stopName || "").toLowerCase().includes(searchLower) ||
        (student.drop?.stopName || "").toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  const sortedStudents = useMemo(() => {
    const sorted = [...filteredStudents].sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === "name") {
        const nameA = (a.studentName || "").toLowerCase();
        const nameB = (b.studentName || "").toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === "admissionNumber") {
        const admA = (a.admissionNumber || "N/A").toString().toLowerCase();
        const admB = (b.admissionNumber || "N/A").toString().toLowerCase();
        comparison = admA.localeCompare(admB);
      }
      
      return sortDirection === "asc" ? comparison : -comparison;
    });
    
    return sorted;
  }, [filteredStudents, sortBy, sortDirection]);
  
  console.log(`StudentVehicleMapping: Filtered ${sortedStudents.length} students from ${groupedStudents.length} (search: "${searchTerm}", assignment filter: "${assignmentFilter}", bus filter: "${busFilter}", sort: "${sortBy}" ${sortDirection})`);

  const totalPages = Math.ceil(sortedStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = sortedStudents.slice(startIndex, endIndex);

  if (loading) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-12">
          <div className="flex flex-col justify-center items-center h-64 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground font-medium">Loading student vehicle mappings...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold mb-2 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                    Student Vehicle Route Mappings
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Manage and view student assignments to vehicle routes, shifts, and stops
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative flex-1 min-w-[280px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, admission, email, vehicle, or stop..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-11 border-2 focus:border-primary transition-colors"
                  />
                </div>
                
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={fetchVehicleRoutes} 
                      variant="outline" 
                      size="icon"
                      className="h-11 w-11 border-2 hover:border-primary transition-colors"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Refresh data</p>
                  </TooltipContent>
                </Tooltip>

                <DropdownMenu>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" className="h-11 w-11 border-2 hover:border-primary transition-colors">
                          <Download className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Export data</p>
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={handleExportCSV} className="cursor-pointer">
                      <FileText className="h-4 w-4 mr-2" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportExcel("xlsx")} className="cursor-pointer">
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Export as xlsx
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExportExcel("xls")} className="cursor-pointer">
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Export as xls
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={downloadTemplate} 
                      variant="outline" 
                      size="icon"
                      className="h-11 w-11 border-2 hover:border-primary transition-colors"
                    >
                      <FileCheck className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download template</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      onClick={handleImportClick} 
                      disabled={isImporting}
                      variant="default"
                      size="icon"
                      className="h-11 w-11"
                    >
                      {isImporting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isImporting ? "Importing..." : "Import from Excel"}</p>
                  </TooltipContent>
                </Tooltip>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                />
              </div>

              {error && (
                <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}
              
              {showImportDialog && importResult && (
                <div className="mb-4 p-6 border-2 rounded-xl bg-gradient-to-br from-background to-muted/20 shadow-xl">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-bold text-xl flex items-center gap-2">
                      <FileCheck className="h-5 w-5 text-primary" />
                      Import Results
                    </h4>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={() => setShowImportDialog(false)}
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Close</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <p className="text-xs font-semibold text-green-900 uppercase tracking-wide">Successful</p>
                      </div>
                      <p className="text-3xl font-bold text-green-700">{importResult.successful}</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 mb-1">
                        <X className="h-4 w-4 text-red-600" />
                        <p className="text-xs font-semibold text-red-900 uppercase tracking-wide">Failed</p>
                      </div>
                      <p className="text-3xl font-bold text-red-700">{importResult.failed}</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg border border-yellow-200">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                        <p className="text-xs font-semibold text-yellow-900 uppercase tracking-wide">Warnings</p>
                      </div>
                      <p className="text-3xl font-bold text-yellow-700">{importResult.warnings}</p>
                    </div>
                  </div>

                  {importResult.successfulMappings && importResult.successfulMappings.length > 0 && (
                    <div className="mb-4 p-4 bg-green-50/50 rounded-lg border border-green-200">
                      <h5 className="font-semibold text-sm text-green-800 mb-3 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Successfully Mapped ({importResult.successfulMappings.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                        {importResult.successfulMappings.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="py-2 px-3 bg-white rounded border border-green-100">
                            <span className="font-medium text-green-900">{item.studentName}</span>
                            <span className="text-green-700"> → {item.vehicle} / {item.shift} / {item.stop}</span>
                          </div>
                        ))}
                        {importResult.successfulMappings.length > 5 && (
                          <p className="text-green-700 mt-2 font-medium">
                            ... and {importResult.successfulMappings.length - 5} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {importResult.warningsList && importResult.warningsList.length > 0 && (
                    <div className="mb-4 p-4 bg-yellow-50/50 rounded-lg border border-yellow-200">
                      <h5 className="font-semibold text-sm text-yellow-800 mb-3 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Warnings ({importResult.warningsList.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                        {importResult.warningsList.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="py-2 px-3 bg-white rounded border border-yellow-100">
                            <span className="font-medium text-yellow-900">Row {item.row}:</span> {item.studentName} - <span className="text-yellow-800">{item.message}</span>
                          </div>
                        ))}
                        {importResult.warningsList.length > 5 && (
                          <p className="text-yellow-700 mt-2 font-medium">
                            ... and {importResult.warningsList.length - 5} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {importResult.failedMappings && importResult.failedMappings.length > 0 && (
                    <div className="mb-4 p-4 bg-red-50/50 rounded-lg border border-red-200">
                      <h5 className="font-semibold text-sm text-red-800 mb-3 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        Failed ({importResult.failedMappings.length})
                      </h5>
                      <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                        {importResult.failedMappings.slice(0, 10).map((item, idx) => (
                          <div key={idx} className="py-2 px-3 bg-white rounded border border-red-100">
                            <span className="font-medium text-red-900">Row {item.row}:</span> {item.studentName || item.admissionNumber || 'Unknown'} - <span className="text-red-800">{item.reason}</span>
                          </div>
                        ))}
                        {importResult.failedMappings.length > 10 && (
                          <p className="text-red-700 mt-2 font-medium">
                            ... and {importResult.failedMappings.length - 10} more
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={() => setShowImportDialog(false)} 
                    variant="outline"
                    className="w-full mt-4 border-2"
                  >
                    Close
                  </Button>
                </div>
              )}
            </div>

            {groupedStudents.length === 0 ? (
              <div className="text-center py-16">
                <Bus className="h-16 w-16 mx-auto text-muted-foreground/40 mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No student vehicle mappings found</p>
                <p className="text-sm text-muted-foreground mt-2">Import data to get started</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-3">
                  {vehicleRoutes.map((route) => (
                    <div key={route.routeId} className="border-2 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 bg-gradient-to-r from-background to-muted/10">
                      <button
                        onClick={() =>
                          setExpandedRoute(
                            expandedRoute === route.routeId ? null : route.routeId
                          )
                        }
                        className="w-full p-5 bg-gradient-to-r from-muted/50 to-muted/30 hover:from-muted/70 hover:to-muted/50 transition-all flex justify-between items-center group"
                      >
                        <div className="text-left flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <Bus className="h-5 w-5 text-primary" />
                            </div>
                            <h4 className="font-bold text-lg group-hover:text-primary transition-colors">
                              {route.vehicleNumber}
                              <span className="text-sm font-normal text-muted-foreground ml-2">
                                ({route.vehicleType})
                              </span>
                            </h4>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground ml-12">
                            {route.mappings?.length > 0 && (
                              <div className="flex items-center gap-2 text-primary">
                                <Users className="h-4 w-4" />
                                <span className="font-semibold">
                                  Students: {new Set(route.mappings.map(m => m.studentId || m.studentEmail || m.studentName)).size}
                                </span>
                              </div>
                            )}
                            {route.capacity > 0 && (
                              <div className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                <span>Capacity: {route.capacity}</span>
                              </div>
                            )}
                            {route.pickupCount > 0 && (
                              <div className="flex items-center gap-2 text-blue-600">
                                <Clock className="h-4 w-4" />
                                <span className="font-semibold">Pickup: {route.pickupCount}</span>
                              </div>
                            )}
                            {route.dropCount > 0 && (
                              <div className="flex items-center gap-2 text-orange-600">
                                <Clock className="h-4 w-4" />
                                <span className="font-semibold">Drop: {route.dropCount}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="ml-4 p-2 rounded-lg bg-background/50 group-hover:bg-background transition-colors">
                          {expandedRoute === route.routeId ? (
                            <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          )}
                        </div>
                      </button>

                      {expandedRoute === route.routeId && route.mappings.length > 0 && (
                        <div className="overflow-x-auto bg-background">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/30">
                                <TableHead className="font-semibold">Student Name</TableHead>
                                <TableHead className="font-semibold">Shift</TableHead>
                                <TableHead className="font-semibold">Pickup/Drop</TableHead>
                                <TableHead className="font-semibold">Stop Location</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {route.mappings.map((mapping, idx) => (
                                <TableRow key={`${route.routeId}-${idx}`} className="hover:bg-muted/50 transition-colors">
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-2">
                                      <User className="h-4 w-4 text-muted-foreground" />
                                      {mapping.studentName}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      {mapping.shiftName}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                                      mapping.pickupDrop === "Pickup"
                                        ? "bg-blue-100 text-blue-800 border border-blue-200"
                                        : "bg-orange-100 text-orange-800 border border-orange-200"
                                    }`}>
                                      {mapping.pickupDrop === "Pickup" ? "↑" : "↓"}
                                      {mapping.pickupDrop}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <MapPin className="h-3 w-3 text-muted-foreground" />
                                      {mapping.stopName}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-10 pt-8 border-t-2">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h4 className="text-xl font-bold mb-2 flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        Detailed View - All Students
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Each student is shown once with their pickup and drop routes (if assigned)
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold">
                        Showing {sortedStudents.length} of {groupedStudents.length} students
                      </p>
                      {(searchTerm || assignmentFilter !== "all" || busFilter !== "all") && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap justify-end">
                          <Filter className="h-3 w-3" />
                          {searchTerm && `Search: "${searchTerm}"`}
                          {searchTerm && (assignmentFilter !== "all" || busFilter !== "all") && " | "}
                          {assignmentFilter !== "all" && `Assignment: ${assignmentFilter.replace("-", " ")}`}
                          {assignmentFilter !== "all" && busFilter !== "all" && " | "}
                          {busFilter !== "all" && `Bus: ${busFilter}`}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 items-center mb-6 p-4 bg-muted/30 rounded-lg border">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-muted-foreground" />
                      <label htmlFor="assignment-filter" className="text-sm font-medium whitespace-nowrap">
                        Assignment:
                      </label>
                      <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
                        <SelectTrigger id="assignment-filter" className="w-[180px] border-2">
                          <SelectValue placeholder="All Students" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Students</SelectItem>
                          <SelectItem value="assigned">Assigned</SelectItem>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          <SelectItem value="pickup-only">Pickup Only</SelectItem>
                          <SelectItem value="drop-only">Drop Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-2">
                      <Bus className="h-4 w-4 text-muted-foreground" />
                      <label htmlFor="bus-filter" className="text-sm font-medium whitespace-nowrap">
                        Bus/Vehicle:
                      </label>
                      <Select value={busFilter} onValueChange={setBusFilter}>
                        <SelectTrigger id="bus-filter" className="w-[150px] border-2">
                          <SelectValue placeholder="All Buses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Buses</SelectItem>
                          {uniqueVehicleNumbers.map((vehicleNum) => (
                            <SelectItem key={vehicleNum} value={vehicleNum}>
                              {vehicleNum}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-2">
                      {sortDirection === "asc" ? (
                        <SortAsc className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <SortDesc className="h-4 w-4 text-muted-foreground" />
                      )}
                      <label htmlFor="sort-by" className="text-sm font-medium whitespace-nowrap">
                        Sort by:
                      </label>
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger id="sort-by" className="w-[160px] border-2">
                          <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="name">Student Name</SelectItem>
                          <SelectItem value="admissionNumber">Admission Number</SelectItem>
                        </SelectContent>
                      </Select>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                            className="h-9 w-9 border-2"
                          >
                            {sortDirection === "asc" ? (
                              <ArrowUp className="h-4 w-4" />
                            ) : (
                              <ArrowDown className="h-4 w-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{sortDirection === "asc" ? "Ascending - Click for Descending" : "Descending - Click for Ascending"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-auto">
                      <label htmlFor="items-per-page" className="text-sm font-medium whitespace-nowrap">
                        Items per page:
                      </label>
                      <Select
                        value={itemsPerPage.toString()}
                        onValueChange={(value) => {
                          setItemsPerPage(Number.parseInt(value));
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger id="items-per-page" className="w-[80px] border-2">
                          <SelectValue placeholder="10" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5</SelectItem>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-lg border-2">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="w-[180px] font-semibold">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => {
                                    if (sortBy === "name") {
                                      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                                    } else {
                                      setSortBy("name");
                                      setSortDirection("asc");
                                    }
                                  }}
                                  className="flex items-center gap-2 hover:text-primary transition-colors"
                                >
                                  <User className="h-4 w-4" />
                                  Student Name
                                  {sortBy === "name" && (
                                    sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Sort by student name</p>
                              </TooltipContent>
                            </Tooltip>
                          </TableHead>
                          <TableHead className="w-[150px] font-semibold">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => {
                                    if (sortBy === "admissionNumber") {
                                      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                                    } else {
                                      setSortBy("admissionNumber");
                                      setSortDirection("asc");
                                    }
                                  }}
                                  className="flex items-center gap-2 hover:text-primary transition-colors"
                                >
                                  Admission Number
                                  {sortBy === "admissionNumber" && (
                                    sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Sort by admission number</p>
                              </TooltipContent>
                            </Tooltip>
                          </TableHead>
                          <TableHead colSpan="3" className="bg-blue-50/80 text-center border-x-2 font-semibold">Pickup Details</TableHead>
                          <TableHead colSpan="3" className="bg-orange-50/80 text-center border-x-2 font-semibold">Drop Details</TableHead>
                        </TableRow>
                        <TableRow className="bg-muted/30">
                          <TableHead className="border-t-2"></TableHead>
                          <TableHead className="border-t-2"></TableHead>
                          <TableHead className="border-t-2 border-l-2 bg-blue-50/80">Vehicle</TableHead>
                          <TableHead className="border-t-2 bg-blue-50/80">Shift</TableHead>
                          <TableHead className="border-t-2 border-r-2 bg-blue-50/80">Stop</TableHead>
                          <TableHead className="border-t-2 border-l-2 bg-orange-50/80">Vehicle</TableHead>
                          <TableHead className="border-t-2 bg-orange-50/80">Shift</TableHead>
                          <TableHead className="border-t-2 border-r-2 bg-orange-50/80">Stop</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedStudents.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan="8" className="text-center py-12 text-muted-foreground">
                              <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
                              <p className="font-medium text-lg">
                                {searchTerm || assignmentFilter !== "all" || busFilter !== "all"
                                  ? "No students match the current filters" 
                                  : "No students mapped"}
                              </p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedStudents.map((student, idx) => {
                            const hasPickup = !!student.pickup;
                            const hasDrop = !!student.drop;
                            const hasBoth = hasPickup && hasDrop;
                            
                            return (
                              <TableRow key={student.studentId || student.studentEmail || idx} className="hover:bg-muted/30 transition-colors">
                                <TableCell className="font-medium">
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-2">
                                      <User className="h-4 w-4 text-muted-foreground" />
                                      <span>{student.studentName}</span>
                                    </div>
                                    <div className="flex gap-1.5">
                                      {hasBoth ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs bg-green-100 text-green-800 font-semibold border border-green-200">
                                          <CheckCircle2 className="h-3 w-3" />
                                          Both Assigned
                                        </span>
                                      ) : hasPickup ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800 font-semibold border border-blue-200">
                                          ↑ Pickup Only
                                        </span>
                                      ) : hasDrop ? (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs bg-orange-100 text-orange-800 font-semibold border border-orange-200">
                                          ↓ Drop Only
                                        </span>
                                      ) : null}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm">{student.admissionNumber || "N/A"}</TableCell>
                                
                                <TableCell className="border-l-2 bg-blue-50/20">
                                  {student.pickup ? (
                                    <div className="text-sm">
                                      <div className="flex items-center gap-1.5 font-medium">
                                        <Bus className="h-3.5 w-3.5 text-blue-600" />
                                        {student.pickup.vehicleNumber}
                                      </div>
                                      <span className="text-xs text-muted-foreground block mt-1">
                                        {student.pickup.vehicleType}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground italic">Not assigned</span>
                                  )}
                                </TableCell>
                                <TableCell className="bg-blue-50/20">
                                  {student.pickup ? (
                                    <div className="flex items-center gap-1.5 text-sm">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      {student.pickup.shiftName}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="border-r-2 bg-blue-50/20">
                                  {student.pickup ? (
                                    <div className="flex items-center gap-1.5 text-sm">
                                      <MapPin className="h-3 w-3 text-muted-foreground" />
                                      {student.pickup.stopName}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                
                                <TableCell className="border-l-2 bg-orange-50/20">
                                  {student.drop ? (
                                    <div className="text-sm">
                                      <div className="flex items-center gap-1.5 font-medium">
                                        <Bus className="h-3.5 w-3.5 text-orange-600" />
                                        {student.drop.vehicleNumber}
                                      </div>
                                      <span className="text-xs text-muted-foreground block mt-1">
                                        {student.drop.vehicleType}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground italic">Not assigned</span>
                                  )}
                                </TableCell>
                                <TableCell className="bg-orange-50/20">
                                  {student.drop ? (
                                    <div className="flex items-center gap-1.5 text-sm">
                                      <Clock className="h-3 w-3 text-muted-foreground" />
                                      {student.drop.shiftName}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="border-r-2 bg-orange-50/20">
                                  {student.drop ? (
                                    <div className="flex items-center gap-1.5 text-sm">
                                      <MapPin className="h-3 w-3 text-muted-foreground" />
                                      {student.drop.stopName}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {filteredStudents.length > 0 && (
                    <div className="flex items-center justify-between mt-6 pt-6 border-t-2">
                      <div className="text-sm text-muted-foreground font-medium">
                        Showing {startIndex + 1} to {Math.min(endIndex, filteredStudents.length)} of {filteredStudents.length} students
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="flex items-center border-2 rounded-lg overflow-hidden">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="h-9 px-3 rounded-r-none border-r-0"
                              >
                                <ArrowUp className="h-4 w-4 rotate-[-90deg]" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Previous page</p>
                            </TooltipContent>
                          </Tooltip>

                          <div className="flex items-center border-x">
                            {(() => {
                              const maxButtons = 5;
                              let startPage = Math.max(
                                1,
                                currentPage - Math.floor(maxButtons / 2)
                              );
                              const endPage = Math.min(
                                totalPages,
                                startPage + maxButtons - 1
                              );

                              if (endPage - startPage + 1 < maxButtons) {
                                startPage = Math.max(1, endPage - maxButtons + 1);
                              }

                              const pages = [];

                              if (startPage > 1) {
                                pages.push(
                                  <Button
                                    key="1"
                                    variant={currentPage === 1 ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setCurrentPage(1)}
                                    className="h-9 w-9 p-0 rounded-none border-0"
                                  >
                                    1
                                  </Button>
                                );
                                if (startPage > 2) {
                                  pages.push(
                                    <div
                                      key="ellipsis1"
                                      className="h-9 w-9 flex items-center justify-center text-muted-foreground"
                                    >
                                      ...
                                    </div>
                                  );
                                }
                              }

                              for (let i = startPage; i <= endPage; i++) {
                                pages.push(
                                  <Button
                                    key={i}
                                    variant={currentPage === i ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setCurrentPage(i)}
                                    className="h-9 w-9 p-0 rounded-none border-0"
                                  >
                                    {i}
                                  </Button>
                                );
                              }

                              if (endPage < totalPages) {
                                if (endPage < totalPages - 1) {
                                  pages.push(
                                    <div
                                      key="ellipsis2"
                                      className="h-9 w-9 flex items-center justify-center text-muted-foreground"
                                    >
                                      ...
                                    </div>
                                  );
                                }
                                pages.push(
                                  <Button
                                    key={totalPages}
                                    variant={
                                      currentPage === totalPages ? "default" : "ghost"
                                    }
                                    size="sm"
                                    onClick={() => setCurrentPage(totalPages)}
                                    className="h-9 w-9 p-0 rounded-none border-0"
                                  >
                                    {totalPages}
                                  </Button>
                                );
                              }

                              return pages;
                            })()}
                          </div>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setCurrentPage((prev) =>
                                    Math.min(prev + 1, totalPages)
                                  )
                                }
                                disabled={currentPage === totalPages || filteredStudents.length === 0}
                                className="h-9 px-3 rounded-l-none border-l-0"
                              >
                                <ArrowDown className="h-4 w-4 rotate-[-90deg]" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Next page</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Go to:</span>
                          <Input
                            type="number"
                            min="1"
                            max={Math.max(1, totalPages)}
                            value={currentPage}
                            onChange={(e) => {
                              const value = Number.parseInt(e.target.value);
                              if (
                                !isNaN(value) &&
                                value >= 1 &&
                                value <= totalPages
                              ) {
                                setCurrentPage(value);
                              }
                            }}
                            className="w-20 h-9 border-2 text-center"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default StudentVehicleMapping;
